import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  calendarApi,
  type CalendarEvent,
  type CalendarKind,
  type CalendarDateType,
} from "@/api/calendar";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { TextInput } from "@/components/ui/Field";
import { Status } from "@/components/ui/Status";
import { cn } from "@/lib/cn";

/**
 * Calendar / upcoming-releases page (issue #9).
 *
 * Unified flat-list view across movies / albums / episodes. The
 * backend hands us a `CalendarEvent[]` already sorted by date; the
 * page groups it into "── Wed, Apr 30 ──" buckets and renders one
 * row per event. Filtering by kind happens client-side so the chips
 * feel instant — the request is bounded server-side at 1000 rows so
 * the UI doesn't fall over on a huge library.
 *
 * Why flat list (not month grid)?
 *
 *   The user's mental model on this page is "what's coming up next" —
 *   a chronological list answers that in two glances. A month grid
 *   would require building a separate render path for each kind +
 *   tooltips for collisions, which is a lot of UI for a P3 surface.
 *   The grouped-by-date list keeps poster + title + chip on one row
 *   and stays readable even on weeks with 30 episode events.
 */

/** Default range — matches the backend default so no params are needed. */
function defaultRange(): { from: string; to: string } {
  const today = new Date();
  const plus60 = new Date(today);
  plus60.setDate(today.getDate() + 60);
  return { from: iso(today), to: iso(plus60) };
}

function iso(d: Date): string {
  // YYYY-MM-DD in local time. The backend does string BETWEEN against
  // ISO dates so we just need the calendar date, not a full timestamp.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const KIND_LABEL: Record<CalendarKind, string> = {
  movie: "Movies",
  album: "Albums",
  episode: "Episodes",
};

export function Calendar() {
  const [range, setRange] = useState(defaultRange);
  const [enabled, setEnabled] = useState<Record<CalendarKind, boolean>>({
    movie: true,
    album: true,
    episode: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["calendar", range.from, range.to],
    queryFn: () => calendarApi.list({ from: range.from, to: range.to }),
  });

  const filtered = useMemo<CalendarEvent[]>(() => {
    if (!data) return [];
    return data.filter((e) => enabled[e.kind]);
  }, [data, enabled]);

  // Keep the dates already-sorted by the server but group them for
  // rendering. We deliberately use a Map so insertion order matches
  // chronological order without re-sorting on the client.
  const grouped = useMemo<Array<[string, CalendarEvent[]]>>(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of filtered) {
      const list = m.get(e.date);
      if (list) list.push(e);
      else m.set(e.date, [e]);
    }
    return Array.from(m.entries());
  }, [filtered]);

  const totalSelected = filtered.length;
  const totalAll = data?.length ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Upcoming"
        title="Calendar"
        description={subhead(range, totalSelected, totalAll, isLoading)}
      />

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(KIND_LABEL) as CalendarKind[]).map((k) => (
            <KindToggle
              key={k}
              kind={k}
              active={enabled[k]}
              onToggle={() =>
                setEnabled((prev) => ({ ...prev, [k]: !prev[k] }))
              }
            />
          ))}
        </div>

        <div className="ml-auto flex items-end gap-2">
          <DateField
            label="From"
            value={range.from}
            onChange={(v) => setRange((r) => ({ ...r, from: v }))}
          />
          <DateField
            label="To"
            value={range.to}
            onChange={(v) => setRange((r) => ({ ...r, to: v }))}
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton.Rows count={8} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          glyph="default"
          title="Nothing on the horizon"
          description="No monitored movies, albums, or episodes are scheduled in this window. Try widening the range."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          glyph="default"
          title="No matches"
          description="Toggle on the kinds you want to see, or widen the date range."
        />
      ) : (
        <div className="space-y-6">
          {grouped.map(([date, events]) => (
            <DayGroup key={date} date={date} events={events} />
          ))}
        </div>
      )}
    </div>
  );
}

function subhead(
  range: { from: string; to: string },
  selected: number,
  total: number,
  loading: boolean,
): string {
  if (loading) return "Loading the next window…";
  const days = daysBetween(range.from, range.to);
  const eventLabel = selected === 1 ? "event" : "events";
  if (selected === total) {
    return `${range.from} → ${range.to} · ${days} day${days === 1 ? "" : "s"} · ${total} ${eventLabel}`;
  }
  return `${range.from} → ${range.to} · ${days} days · ${selected} of ${total} events`;
}

function daysBetween(from: string, to: string): number {
  const a = Date.parse(from);
  const b = Date.parse(to);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

function KindToggle({
  kind,
  active,
  onToggle,
}: {
  kind: CalendarKind;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        "focus-ring inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.12em] transition",
        active
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-line bg-surface-2/40 text-foreground/55 hover:border-line-strong hover:text-foreground",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          active ? "bg-accent" : "bg-foreground/30",
        )}
      />
      {KIND_LABEL[kind]}
    </button>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
        {label}
      </span>
      <TextInput
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-40"
      />
    </label>
  );
}

function DayGroup({
  date,
  events,
}: {
  date: string;
  events: CalendarEvent[];
}) {
  return (
    <section>
      <DayHeader date={date} count={events.length} />
      <ul className="overflow-hidden rounded-md border border-line bg-surface/30 divide-y divide-line/60">
        {events.map((e) => (
          <li key={`${e.kind}:${e.media_id}:${e.date_type}:${e.date}`}>
            <EventRow event={e} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** "── Wed, Apr 30 ──" — header above each day's group. */
function DayHeader({ date, count }: { date: string; count: number }) {
  // Date.parse on a bare YYYY-MM-DD treats it as UTC; build a local
  // Date so the weekday lines up with the user's calendar instead of
  // jumping backwards in negative-UTC zones.
  const [y, m, d] = date.split("-").map(Number);
  const local = new Date(y, (m ?? 1) - 1, d ?? 1);
  const fmt = local.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return (
    <div className="mb-2 flex items-center gap-3 px-1">
      <span aria-hidden className="h-px w-6 bg-line" />
      <span className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/65">
        {fmt}
      </span>
      <span className="font-mono text-[10px] tabular-nums text-foreground/40">
        {count} event{count === 1 ? "" : "s"}
      </span>
      <span aria-hidden className="ml-1 h-px flex-1 bg-line/60" />
    </div>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  return (
    <Link
      to={hrefFor(event)}
      className="group flex items-center gap-3 px-3 py-2.5 transition hover:bg-surface-2/40 focus-ring"
    >
      {event.poster_url ? (
        <img
          src={event.poster_url}
          alt=""
          className="aspect-[2/3] w-10 shrink-0 rounded border border-line object-cover"
          loading="lazy"
        />
      ) : (
        <div className="aspect-[2/3] w-10 shrink-0 rounded border border-line bg-surface-2/40" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-sm font-medium tracking-display-tight">
          {event.title}
        </div>
        <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/45">
          <span>{event.kind}</span>
          {event.season_number !== null && event.episode_number !== null && (
            <>
              <span aria-hidden>·</span>
              <span>
                S{String(event.season_number).padStart(2, "0")}E
                {String(event.episode_number).padStart(2, "0")}
              </span>
            </>
          )}
        </div>
      </div>
      <DateTypeChip dateType={event.date_type} />
    </Link>
  );
}

function DateTypeChip({ dateType }: { dateType: CalendarDateType }) {
  // Map the discriminator onto a tone. Theatrical is the "early" date
  // for a movie (warn = anticipation), digital is the actionable one
  // (good = grabs likely available), physical is the "late" disc one
  // (info), album release is accent (brand-y), air is neutral.
  const tone =
    dateType === "theatrical"
      ? "warn"
      : dateType === "digital"
        ? "good"
        : dateType === "physical"
          ? "info"
          : dateType === "release"
            ? "accent"
            : "neutral";
  return (
    <Status variant="chip" tone={tone}>
      {dateType}
    </Status>
  );
}

function hrefFor(e: CalendarEvent): string {
  switch (e.kind) {
    case "movie":
      return `/movies/${e.media_id}`;
    case "album":
      return `/music/albums/${e.media_id}`;
    case "episode":
      // Episodes deep-link to the parent series — there's no per-episode
      // detail page yet.
      return `/tv/${e.series_id ?? e.media_id}`;
  }
}
